<template>
  <v-container
    class="fill-height pa-2"
    fluid
  >
    <v-row
      v-if="inbox.length > 0"
    >
      <v-col
        lg="3"
        md="4"
        sm="12"
      >
        <v-card>
          <v-toolbar
            flat
            height="80"
          >
            <v-avatar :color="$t('Message.Toolbar.Color')">
              <v-icon color="indigo lighten-5">
                {{ $t('Message.Toolbar.Icon') }}
              </v-icon>
            </v-avatar>

            <v-toolbar-title
              class="pl-3"
            >
              <div class="body-3">
                {{ $tc('Message.Title', 0) }}
              </div>
              <div class="text-caption">
                {{ $t('Message.Toolbar.Subtitle') }}
              </div>
            </v-toolbar-title>
            <v-spacer />
            <v-menu offset-y>
              <template #activator="{on}">
                <v-btn
                  class="mx-0"
                  icon
                  small
                  v-on="on"
                >
                  <v-icon dark>
                    {{ $t('Message.Toolbar.Button.Filter.Icon') }}
                  </v-icon>
                </v-btn>
              </template>

              <v-list
                dense
                subheader
              >
                <v-subheader>{{ $t('Message.Toolbar.Button.Filter.Title') }}</v-subheader>
                <v-divider />
                <v-list-item
                  :class="[{'primary--text': filterByProperty===''}]"
                  :inactive="filterByProperty===''"
                  @click="filterByProperty=''"
                >
                  <v-list-item-title>{{ $t('Common.Misc.All') }}</v-list-item-title>
                </v-list-item>
                <v-list-item
                  v-for="group in groups"
                  :key="group.Id"
                  :class="[{'primary--text': filterByProperty.toString()===group.Id.toString()}]"
                  :inactive="filterByProperty.toString()===group.Id.toString()"
                  @click="filterByProperty=group.Id"
                >
                  <v-list-item-title>{{ group.Name }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <v-menu offset-y>
              <template #activator="{on}">
                <v-btn
                  class="mx-0"
                  icon
                  small
                  v-on="on"
                >
                  <v-icon dark>
                    {{ $t('Message.Toolbar.Button.Group.Icon') }}
                  </v-icon>
                </v-btn>
              </template>
              <v-list
                dense
                subheader
              >
                <v-subheader>{{ $t('Message.Toolbar.Button.Group.Title') }}</v-subheader>
                <v-divider />
                <v-list-item
                  v-for="(group, index) in groupByGroups"
                  :key="index"
                  :class="[{'primary--text': groupByProperty.toString()===group.property.toString()}]"
                  :inactive="groupByProperty.toString()===group.property.toString()"
                  @click="groupByProperty=group.property"
                >
                  <v-list-item-title>{{ group.title }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <v-btn
              v-if="userIsSuperAdmin"
              class="mx-0"
              icon
              small
              @click="createNew"
            >
              <v-icon dark>
                {{ $t('Message.Toolbar.Button.New.Icon') }}
              </v-icon>
            </v-btn>
          </v-toolbar>
          <v-divider />
          <v-card-text
            ref="messageList"
            class="pa-0"
            style="overflow-y: auto;"
          >
            <v-list
              style="overflow-y: auto;"
              two-line
            >
              <template v-for="(group, key) in messagesFilteredAndGrouped">
                <v-subheader :key="'header' + key">
                  <span v-if="groupByProperty.toString() === groupByGroups.date.property.toString()">{{ key }}</span>
                  <span v-else-if="groupByProperty.toString() === groupByGroups.read.property.toString()">
                    {{ (key === 'true') ? $t('Message.Toolbar.Label.Reading.Read') : $t('Message.Toolbar.Label.Reading.Unread') }}
                  </span>
                  <span v-else-if="groupByProperty.toString() === groupByGroups.important.property.toString()">
                    {{ (key === 'true') ? $t('Message.Toolbar.Label.Importance.Important') : $t('Message.Toolbar.Label.Importance.Regular') }}
                  </span>
                  <span v-else>
                    {{ key }}
                  </span>
                </v-subheader>
                <template v-for="(message, index) in group">
                  <v-list-item
                    :key="'list-tile-' + message.Id + '-' + key + '-' + index"
                    class="ma-1"
                    ripple
                    @click="showMessage(message)"
                  >
                    <div class="mr-4">
                      <v-badge
                        :value="message.Important"
                        color="red"
                        overlap
                        right
                      >
                        <template #badge>
                          <v-icon

                            dark
                            small
                          >
                            fas fa-exclamation
                          </v-icon>
                        </template>

                        <v-avatar
                          :color="message.Type.Color ? message.Type.Color : 'indigo'"
                          dark
                          size="48"
                        >
                          <v-icon
                            :color="message.Type.Color ? (message.Type.Color + ' lighten-5') : 'indigo lighten-5'"
                            dark
                          >
                            {{ message.Type.Icon }}
                          </v-icon>
                        </v-avatar>

                        <div
                          v-if="userIsSuperAdmin"
                          class="text-center mt-1"
                        >
                          <v-icon
                            v-if="message.Active"
                            class="mt-1"
                            color="green"
                            small
                          >
                            mdi-message
                          </v-icon>

                          <v-icon
                            v-if="message.SendWithEmail"
                            color="blue"
                            small
                          >
                            mdi-email
                          </v-icon>
                        </div>
                      </v-badge>
                    </div>

                    <v-list-item-content>
                      <v-list-item-title :class="[{'font-weight-bold': !message.Read}]">
                        {{ message.Type.Name }}
                      </v-list-item-title>
                      <v-list-item-subtitle :class="[{'font-weight-bold': !message.Read}]">
                        {{ message.Type.Subject }}
                      </v-list-item-subtitle>
                    </v-list-item-content>

                    <v-list-item-action>
                      <v-list-item-action-text>
                        {{ message.DateAdded }}
                      </v-list-item-action-text>

                      <v-list-item-action-text>
                        {{ message.TimeAdded }}
                      </v-list-item-action-text>

                      <div>
                        <v-btn
                          v-if="userIsSuperAdmin"
                          color="red"
                          icon
                          small
                          @click.stop="deleteMessage(message)"
                        >
                          <v-icon size="20">
                            mdi-close-circle-outline
                          </v-icon>
                        </v-btn>

                        <v-btn
                          v-if="userIsSuperAdmin && message?.Source && !message?.SendWithEmail"
                          color="blue"
                          icon
                          small
                          @click.stop="sendWithEmail(message)"
                        >
                          <v-icon size="20">
                            mdi-email-arrow-right
                          </v-icon>
                        </v-btn>

                        <v-btn
                          v-if="userIsSuperAdmin && message?.Source && !message?.Active"
                          color="green"
                          icon
                          small
                          @click.stop="sendWithMessage(message)"
                        >
                          <v-icon size="20">
                            mdi-message-arrow-right
                          </v-icon>
                        </v-btn>

                        <v-btn
                          v-if="userIsSuperAdmin && message?.Source"
                          color="amber"
                          icon
                          small
                          @click.stop="createNewFromMessage(message)"
                        >
                          <v-icon size="18">
                            mdi-message-plus-outline
                          </v-icon>
                        </v-btn>

                        <v-btn
                          icon
                          small
                          @click.stop="showMessage(message, true)"
                        >
                          <v-icon>
                            mdi-fullscreen
                          </v-icon>
                        </v-btn>
                      </div>
                    </v-list-item-action>
                  </v-list-item>
                  <v-divider
                    :key="'divider-' + message.Id + '-' + key + '-' + index"
                    inset
                  />
                </template>
              </template>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col
        class="hidden-sm-and-down"
        lg="9"
        md="8"
      >
        <v-card
          v-if="selectedMessage"
        >
          <v-toolbar
            flat
            height="80"
          >
            <div class="mr-3">
              <v-badge
                :value="selectedMessage.Important"
                color="red"
                overlap
                right
              >
                <template #badge>
                  <v-icon

                    dark
                    small
                  >
                    fas fa-exclamation
                  </v-icon>
                </template>
                <v-avatar :color="selectedMessage.Type.Color ? selectedMessage.Type.Color : 'indigo'">
                  <v-icon :color="selectedMessage.Type.Color ? (selectedMessage.Type.Color + ' lighten-5') : 'indigo lighten-5'">
                    {{ selectedMessage.Type.Icon }}
                  </v-icon>
                </v-avatar>
              </v-badge>
            </div>

            <v-toolbar-title
              class="pl-1"
            >
              <div class="body-3">
                {{ selectedMessage.Type.Name }}
              </div>
              <div class="text-caption">
                {{ selectedMessage.Type.Subject }}
              </div>
            </v-toolbar-title>
            <v-spacer />
            <v-toolbar-title
              class="px-4"
            >
              <div class="text-caption text-right">
                {{ selectedMessage.DateAdded }} {{ selectedMessage.TimeAdded }}
              </div>
            </v-toolbar-title>
            <v-btn
              class="mx-0"
              icon
              @click="toggleFullScreen"
            >
              <v-icon>fullscreen</v-icon>
            </v-btn>
          </v-toolbar>
          <v-divider />

          <v-card-text
            v-if="!renderMessageInIframe"
            ref="messageBody"
            v-dynamic-router-link
            v-safe-html="selectedMessage.Body.replace(new RegExp('{username}', 'g'), posUser.staff_name).replace(new RegExp('{{\\$user_name}}', 'gm'), posUser.staff_name)"
            :style="userIsSuperAdmin ? 'user-select: text;' : ''"
            style="overflow: auto;"
          />

          <v-card-text
            v-else
            ref="messageBody"
          >
            <iframe
              ref="messageFrame"
              style="border: 0; width: 100%; height: 100%"
            />
          </v-card-text>

          <v-divider />
          <v-card-actions class="pa-4">
            <v-spacer />
            <v-btn
              class="light-blue--text text--darken-1"
              outlined
              @click="onCancelButtonClick"
            >
              {{ $t('Common.Button.Close').toLocaleUpperCase($i18n.locale) }}
            </v-btn>
          </v-card-actions>
        </v-card>

        <v-row
          v-else
          class="align-center fill-height"
        >
          <v-col
            class="text-center"
            cols="12"
          >
            <div
              class="grey--text pa-6"
            >
              <v-icon
                class="mb-4"
                color="grey lighten-2"
                size="128"
              >
                {{ $t('Message.ListView.Select.Icon') }}
              </v-icon>
              <div class="text-h6 mb-2">
                {{ $t('Message.ListView.Select.Title') }}
              </div>
              <div class="text-subtitle-1">
                {{ $t('Message.ListView.Select.Subtitle') }}
              </div>
            </div>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-row
      v-else
      align="center"
    >
      <v-col
        class="text-center"
        cols="12"
      >
        <div
          class="grey--text pa-6"
        >
          <v-icon
            class="mb-4"
            color="grey lighten-2"
            size="128"
          >
            {{ $t('Message.ListView.NoData.Icon') }}
          </v-icon>
          <div class="text-h6 mb-2">
            {{ $tc('Message.Title', 0) }}
          </div>
          <div class="text-subtitle-1">
            {{ $t('Message.ListView.NoData.Subtitle') }}
          </div>
          <div class="pa-2">
            <v-btn
              v-if="userIsSuperAdmin"
              color="primary"
              dark
              @click="createNew"
            >
              {{ $t('Message.Toolbar.Button.New.Title').toLocaleUpperCase($i18n.locale) }}
              <v-icon right>
                {{ $t('Message.Toolbar.Button.New.Icon') }}
              </v-icon>
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <message-details-dialog
      :message="selectedMessage"
      :visible.sync="showMessageDialog"
    />

    <message-send-dialog
      v-if="userIsSuperAdmin"
      :message="selectedMessageEdit"
      :message-config="messageConfig"
      :visible.sync="showSendMessageDialog"
    />

    <message-templates-dialog
      v-if="userIsSuperAdmin"
      :visible.sync="messageTemplateDialogVisible"
      @template:selected="onTemplateSelected"
    />

    <confirm-dialog
      v-if="userIsSuperAdmin && selectedMessageDelete"
      :html-content="$t('Message.ConfirmDeleteDialog.Content', {title: selectedMessageDelete.Type.Subject, dateTime: `${selectedMessageDelete.DateAdded} ${selectedMessageDelete.TimeAdded}`})"
      :html-title="$t('Message.ConfirmDeleteDialog.Title')"
      :info-bar="true"
      :info-bar-body="$t('Message.ConfirmDeleteDialog.Warning')"
      :visible.sync="confirmDialogDeleteVisible"
      :width="560"
      no-button-class="v-btn--outline green--text"
      title-class="red white--text lighten-0"
      yes-button-class="red white--text"
      yes-button-event="on-confirm-delete"
      @on-confirm-delete="onConfirmDeleteMessage"
    />

    <confirm-dialog
      v-if="userIsSuperAdmin && selectedMessageSendWithMessage"
      :html-content="selectedMessageSendWithMessage?.To?.length > 0 ? $t('Message.ConfirmSendWithMessageDialog.ContentSpecific', {domains: getMessageToDomains(selectedMessageSendWithMessage), title: selectedMessageSendWithMessage.Type.Subject, dateTime: `${selectedMessageSendWithMessage.DateAdded} ${selectedMessageSendWithMessage.TimeAdded}`}) : $t('Message.ConfirmSendWithMessageDialog.ContentAll', {title: selectedMessageSendWithMessage.Type.Subject, dateTime: `${selectedMessageSendWithMessage.DateAdded} ${selectedMessageSendWithMessage.TimeAdded}`})"
      :html-title="$t('Message.ConfirmSendWithMessageDialog.Title')"
      :visible.sync="confirmDialogSendWithMessageVisible"
      :width="560"
      no-button-class="v-btn--outline green--text"
      title-class="green white--text lighten-0"
      yes-button-class="green white--text"
      yes-button-event="on-confirm-delete"
      @on-confirm-delete="onConfirmSendWithMessageMessage"
    />

    <confirm-dialog
      v-if="userIsSuperAdmin && selectedMessageSendWithEmail"
      :html-content="selectedMessageSendWithEmail?.To?.length > 0 ? $t('Message.ConfirmSendWithEmailDialog.ContentSpecific', {domains: getMessageToDomains(selectedMessageSendWithEmail), title: selectedMessageSendWithEmail.Type.Subject, dateTime: `${selectedMessageSendWithEmail.DateAdded} ${selectedMessageSendWithEmail.TimeAdded}`}) : $t('Message.ConfirmSendWithEmailDialog.ContentAll', {title: selectedMessageSendWithEmail.Type.Subject, dateTime: `${selectedMessageSendWithEmail.DateAdded} ${selectedMessageSendWithEmail.TimeAdded}`})"
      :html-title="$t('Message.ConfirmSendWithEmailDialog.Title')"
      :visible.sync="confirmDialogSendWithEmailVisible"
      :width="560"
      no-button-class="v-btn--outline primary--text"
      title-class="primary white--text lighten-0"
      yes-button-class="primary white--text"
      yes-button-event="on-confirm-delete"
      @on-confirm-delete="onConfirmSendWithEmailMessage"
    />
  </v-container>
</template>

<script>
import moment from 'moment'
import MessageDetailsDialog from './MessageDetailsDialog'
import MessageSendDialog from './MessageSendDialog'
import Resizable from '@/mixins/resizable'
import AppData from '@/mixins/appdata'
import Auth from '@/mixins/auth'
import ConfirmDialog from '@/components/common/ConfirmDialog.vue'
import MessageTemplatesDialog from '@/components/messages/MessageTemplatesDialog.vue'
import {clone} from '@/lib/helper/helper'
import MessageConfigModel from '@/api/Models/messages/MessageConfigModel'

export default {
  name      : 'MessagesInbox',
  components: {
    MessageTemplatesDialog,
    ConfirmDialog,
    MessageSendDialog,
    MessageDetailsDialog
  },
  directives: {},
  mixins    : [AppData, Auth, Resizable],
  props     : [],
  DataStore : {
    messages: 'messages',
    counts  : 'messages.count',
    inbox   : 'messages.inbox'
  },
  data() {
    return {
      confirmDialogSendWithEmailVisible  : false,
      confirmDialogSendWithMessageVisible: false,
      confirmDialogDeleteVisible         : false,
      messageTemplateDialogVisible       : false,
      showSendMessageDialog              : false,
      showMessageDialog                  : false,
      selectedMessage                    : null,
      selectedMessageEdit                : null,
      selectedMessageDelete              : null,
      selectedMessageSendWithEmail       : null,
      selectedMessageSendWithMessage     : null,
      messageConfig                      : new MessageConfigModel(),
      filterByProperty                   : '',
      groupByProperty                    : 'DateAdded',
      groupByGroups                      : {
        date: {
          title   : this.$t('Common.Date.Title'),
          property: 'DateAdded'
        },
        from: {
          title   : this.$t('Message.Toolbar.Label.Sender'),
          property: 'Type.Name'
        },
        read: {
          title   : this.$t('Message.Toolbar.Label.Reading.Read'),
          property: 'Read'
        },
        important: {
          title   : this.$t('Message.Toolbar.Label.Importance.Important'),
          property: 'Important'
        },
        type: {
          title   : this.$t('Message.Toolbar.Label.Type'),
          property: 'Type.GroupName'
        }
      }
    }
  },
  computed: {
    groups() {
      return this.messages.groups.all()
    },
    messagesFilteredAndGrouped() {
      if (this.groupByProperty === 'DateAdded') {
        return this.messagesFilteredAndGroupedByDate
      }
      return this.inbox.filteredAndGrouped(this.filterByProperty, this.groupByProperty)
    },
    messagesFilteredAndGroupedByDate() {
      const retVal = {}
      const items = this.inbox.filteredAndGrouped(this.filterByProperty, this.groupByProperty)
      Object.keys(items).forEach(key => {
        const timeAgo = this.getMessageTimeAgo(key)
        if (!retVal.hasOwnProperty(timeAgo)) retVal[timeAgo] = []
        retVal[timeAgo] = [...retVal[timeAgo], ...items[key]]
      })
      return retVal
    },

    renderMessageInIframe() {
      if (!this.selectedMessage) return false
      const document = new DOMParser().parseFromString(this.selectedMessage.Body, 'text/html')
      return document.head.children.length > 0
    }
  },
  watch: {},
  beforeCreate() {
  },
  created() {
    this.$bus.$on(window.SocketCommand.Message.Delete, this.onDeleteMessageResult)
    this.$bus.$on(window.SocketCommand.Message.SendWithEmail, this.sendWithEmailResult)
    this.$bus.$on(window.SocketCommand.Message.SendWithMessage, this.sendWithMessageResult)
    this.$bus.$on(window.SocketCommand.Ajax.Get, this.onGetAjax)

    this.messageConfig.fetch()
    this.getData()
  },
  beforeMount() {
  },
  mounted() {
  },
  beforeUpdate() {
  },
  beforeDestroy() {
    this.$bus.$off(window.SocketCommand.Message.Delete, this.onDeleteMessageResult)
    this.$bus.$off(window.SocketCommand.Message.SendWithEmail, this.sendWithEmailResult)
    this.$bus.$off(window.SocketCommand.Message.SendWithMessage, this.sendWithMessageResult)
    this.$bus.$off(window.SocketCommand.Ajax.Get, this.onGetAjax)
  },
  destroyed() {
  },
  methods: {
    onGetAjax(payload) {
      const command = window.SocketCommand.Message.All
      const payloadCommandsArray = payload?.command ? Array.isArray(payload?.command) ? payload.command : [payload.command] : []

      if (payloadCommandsArray.length) {
        payloadCommandsArray.forEach(cmd => {
          const payloadCommand = window.SocketCommand.commandToClientFormat(cmd || '')
          if (payload && payloadCommand === command) {
            this.inbox.fetch()
          }
        })
      }
    },

    getData() {
      this.messages.fetchCounts()
      this.inbox.fetch()
    },

    getMessageToDomains(message) {
      const selectedApps = message?.To || []
      const allApps = this.messageConfig.Apps.all() || []
      return selectedApps.map(selectedAppKey => allApps.find(app => app.Key === selectedAppKey)?.Name).join(', ')
    },

    showMessage(message, showInDialog = false) {
      if (this.selectedMessage !== message) {
        message.markAsRead()
      }
      this.selectedMessage = message
      this.setMessageFrameContent(this.selectedMessage.Body)
      if (this.$refs.messageBody) this.$refs.messageBody.scrollTop = 0

      this.showMessageDialog = this.$vuetify.breakpoint.smAndDown || showInDialog
    },

    setMessageFrameContent(htmlContent) {
      this.$nextTick(() => {
        const iframeElement = this.$refs.messageFrame
        if (!iframeElement) return

        const message = htmlContent
          .replace(new RegExp('{username}', 'g'), this.posUser.staff_name)
          .replace(new RegExp('{{\\$user_name}}', 'g'), this.posUser.staff_name)

        iframeElement.src = 'about:blank'
        iframeElement.contentWindow.document.open()
        iframeElement.contentWindow.document.write(message)
        iframeElement.contentWindow.document.close()
        iframeElement.contentWindow.scrollTo(0, 0)
      })
    },

    createNew() {
      this.messageTemplateDialogVisible = true
    },

    onTemplateSelected(data) {
      this.messageTemplateDialogVisible = false

      if (data) {
        this.createNewFromTemplate(data)
      } else {
        this.createNewBlank()
      }
    },

    createNewBlank() {
      this.selectedMessageEdit = null
      this.showSendMessageDialog = true
    },

    createNewFromTemplate(message) {
      this.selectedMessageEdit = clone(message)
      this.showSendMessageDialog = true
    },

    sendWithMessage(message) {
      this.selectedMessageSendWithMessage = message
      this.confirmDialogSendWithMessageVisible = true
    },
    onConfirmSendWithMessageMessage() {
      window.callAS(window.SocketCommand.Message.SendWithMessage, {Id: this.selectedMessageSendWithMessage.Id})
      this.selectedMessageSendWithMessage = null
    },
    sendWithMessageResult(data) {
      if (data.status === 'success') {
        this.getData()

        this.$bus.$emit('app-show-notification', {
          body: this.$t('Message.ListView.Send.Success'),
          type: 'success',
          icon: 'check'
        })
      } else {
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Common.Error.Generic'),
          type: 'error',
          icon: 'warning'
        })
      }
    },

    sendWithEmail(message) {
      this.selectedMessageSendWithEmail = message
      this.confirmDialogSendWithEmailVisible = true
    },
    onConfirmSendWithEmailMessage() {
      window.callAS(window.SocketCommand.Message.SendWithEmail, {Id: this.selectedMessageSendWithEmail.Id})
      this.selectedMessageSendWithEmail = null
    },
    sendWithEmailResult(data) {
      if (data.status === 'success') {
        this.getData()

        this.$bus.$emit('app-show-notification', {
          body: this.$t('Message.ListView.Send.Success'),
          type: 'success',
          icon: 'check'
        })
      } else {
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Common.Error.Generic'),
          type: 'error',
          icon: 'warning'
        })
      }
    },

    createNewFromMessage(message) {
      this.selectedMessageEdit = clone(message)
      this.selectedMessageEdit.Id = null
      this.showSendMessageDialog = true
    },

    deleteMessage(message) {
      this.selectedMessageDelete = message
      this.confirmDialogDeleteVisible = true
    },
    onConfirmDeleteMessage() {
      window.callAS(window.SocketCommand.Message.Delete, {Id: this.selectedMessageDelete.Id})

      this.selectedMessage = null
      this.selectedMessageEdit = null
      this.selectedMessageDelete = null
      this.showSendMessageDialog = false
    },
    onDeleteMessageResult(data) {
      if (data.status === 'success') {
        this.getData()

        this.$bus.$emit('app-show-notification', {
          body: this.$t('Message.ConfirmDeleteDialog.Success'),
          type: 'success',
          icon: 'check'
        })
      } else {
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Common.Error.Generic'),
          type: 'error',
          icon: 'warning'
        })
      }
    },

    toggleFullScreen() {
      this.showMessageDialog = true
    },

    onCancelButtonClick() {
      this.selectedMessage = null
    },

    onResize() {
      const messageList = this.$refs.messageList
      const messageBody = this.$refs.messageBody

      const headerHeight = document.getElementById('appToolbar')?.offsetHeight ? document.getElementById('appToolbar').offsetHeight : 0
      const footerHeight = document.getElementById('appFooter')?.offsetHeight ? document.getElementById('appFooter').offsetHeight : 0

      if (messageList) messageList.style.height = (innerHeight - headerHeight - footerHeight - 81 - 24) + 'px'
      if (messageBody) messageBody.style.height = (innerHeight - headerHeight - footerHeight - 150 - 24) + 'px'
    },

    getMessageTimeSince(dateString) {
      return moment(dateString, 'DD-MM-YYYY').subtract(moment().days())
    },
    getMessageBodyHtml(message) {
      const messageBody = message?.Body || ''

      return messageBody
        .replace(/(<([^>]+)>)/ig, '')
        .replace(new RegExp(this.$t('Message.Toolbar.Label.Dear', 'el') + ' {username},', 'g'), '')
        .replace(new RegExp(this.$t('Message.Toolbar.Label.Dear', 'en') + ' {username},', 'g'), '')
        .replace(new RegExp(this.$t('Message.Toolbar.Label.Dear', 'el') + ' {{\\$user_name}},', 'g'), '')
        .replace(new RegExp(this.$t('Message.Toolbar.Label.Dear', 'en') + ' {{\\$user_name}},', 'g'), '')
    },

    getMessageTimeAgo(key) {
      const now = new Date().getTime()
      const sinceTime = new Date(this.getMessageTimeSince(key)).getTime()
      const seconds = now / 1000 - sinceTime / 1000

      const locales = {
        en: [
          'Just Now',
          ['%s Second', '%s Seconds'],
          ['%s Minute', '%s Minutes'],
          ['%s Hour', '%s Hours'],
          ['%s Day', '%s Days'],
          ['%s Week', '%s Weeks'],
          ['%s Month', '%s Months'],
          ['%s Year', '%s Years'],
          ['Today', 'Today']
        ],
        el: [
          'Τώρα',
          ['%s Δευτερόλεπτο', '%s Δευτερόλεπτα'],
          ['%s Λεπτό', '%s Λεπτά'],
          ['%s Ώρα', '%s Ώρες'],
          ['%s Ημέρα', '%s Ημέρες'],
          ['%s Εβδομάδα', '%s Εβδομάδες'],
          ['%s Μήνα', '%s Μήνες'],
          ['%s Έτος', '%s Έτη'],
          ['Σήμερα', 'Σήμερα']
        ]
      }

      const MINUTE = 60
      const HOUR = MINUTE * 60
      const DAY = HOUR * 24
      const WEEK = DAY * 7
      const MONTH = DAY * 30
      const YEAR = DAY * 365

      function pluralOrSingular(data, locale) {
        if (data === 'just now') {
          return locale
        }

        const count = data < 1 ? Math.ceil(data) : Math.round(data)
        if (Array.isArray(locale)) {
          return count > 1
            ? locale[1].replace(/%s/, count)
            : locale[0].replace(/%s/, count)
        }
        return locale.replace(/%s/, count)
      }

      const ret =
          seconds <= 5
            ? this.minValCalc === 'seconds' ? pluralOrSingular('just now', locales[this.$i18n.locale][0]) : pluralOrSingular('', locales[this.$i18n.locale][8])
            : seconds < MINUTE
              ? this.minValCalc === 'seconds' ? pluralOrSingular(seconds, locales[this.$i18n.locale][1]) : pluralOrSingular('', locales[this.$i18n.locale][8])
              : seconds < HOUR
                ? this.minValCalc === 'seconds' ? pluralOrSingular(seconds / MINUTE, locales[this.$i18n.locale][2]) : pluralOrSingular('', locales[this.$i18n.locale][8])
                : seconds < DAY
                  ? this.minValCalc === 'seconds' ? pluralOrSingular(seconds / HOUR, locales[this.$i18n.locale][3]) : pluralOrSingular('', locales[this.$i18n.locale][8])
                  : seconds < WEEK
                    ? pluralOrSingular((seconds / DAY) - 1, locales[this.$i18n.locale][4])
                    : seconds < MONTH
                      ? pluralOrSingular(seconds / WEEK, locales[this.$i18n.locale][5])
                      : seconds < YEAR
                        ? pluralOrSingular(
                          seconds / MONTH,
                          locales[this.$i18n.locale][6]
                        )
                        : pluralOrSingular(
                          seconds / YEAR,
                          locales[this.$i18n.locale][7]
                        )

      return ret
    }
  }
}
</script>

<style scoped>

</style>
